import {
  CancelRounded,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadRounded,
  HelpOutlined,
} from "@mui/icons-material";
import { Box, Button } from "@mui/material";

import type { OrderStatus } from "../../types/order";

const TERMINAL_STATUSES: OrderStatus[] = ["CANCELLED", "CREATED"];

export const BUTTON_COLORS = {
  delete: {
    border: "#BE2217",
    color: "#BE2217",
    hoverBorder: "#A31B12",
    hoverBg: "rgba(190,34,23,0.05)",
  },
  cancel: {
    bg: "#BE2217",
    color: "#FFF",
    hoverBg: "#A31B12",
  },
  request: {
    bg: "#3D3D3D",
    color: "#FFF",
    hoverBg: "#2A2A2A",
  },
  download: {
    bg: "#111111",
    color: "#FFF",
    hoverBg: "#000",
  },
  approve: {
    bg: "#D4EDFF",
    color: "#0054FC",
    hoverBg: "#B8DEFF",
    borderRadius: "100px",
    fontSize: 16,
  },
} as const;

export const getButtonSx = (variant: keyof typeof BUTTON_COLORS) => {
  if (variant === "delete") {
    const colors = BUTTON_COLORS.delete;
    return {
      height: 40,
      borderRadius: "96px",
      borderColor: colors.border,
      color: colors.color,
      fontWeight: 700,
      fontSize: 14,
      textTransform: "none" as const,
      whiteSpace: "nowrap" as const,
      "&:hover": {
        borderColor: colors.hoverBorder,
        bgcolor: colors.hoverBg,
      },
    };
  }

  const colors =
    BUTTON_COLORS[variant as Exclude<keyof typeof BUTTON_COLORS, "delete">];
  return {
    height: 40,
    borderRadius: "96px",
    bgcolor: colors.bg,
    color: colors.color,
    fontWeight: 700,
    fontSize: 14,
    textTransform: "none" as const,
    whiteSpace: "nowrap" as const,
    boxShadow: "none",
    "&:hover": {
      bgcolor: colors.hoverBg,
      boxShadow: "none",
    },
  };
};

export interface ActionButtonsGroupProps {
  selectedIds: string[];
  selectedStatuses: OrderStatus[];
  onDelete: () => void;
  onCancel: () => void;
  onRequestBulk: () => void;
  onDownloadXml: () => void;
  onApproveBulk: () => void;
}

export const ActionButtonsGroup = ({
  selectedIds,
  selectedStatuses,
  onDelete,
  onCancel,
  onRequestBulk,
  onDownloadXml,
  onApproveBulk,
}: ActionButtonsGroupProps) => {
  if (selectedIds.length === 0) return null;

  const allTerminal =
    selectedStatuses.length > 0 &&
    selectedStatuses.every((s) => TERMINAL_STATUSES.includes(s));
  const allTerminalOrRequestMore =
    selectedStatuses.length > 0 &&
    selectedStatuses.every(
      (s) => TERMINAL_STATUSES.includes(s) || s === "REQUEST_MORE_INFO",
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      {!allTerminal && (
        <Button
          variant="outlined"
          startIcon={<DeleteOutlined />}
          onClick={onDelete}
          sx={{
            height: 40,
            borderRadius: "96px",
            borderColor: BUTTON_COLORS.delete.border,
            color: BUTTON_COLORS.delete.color,
            fontWeight: 700,
            fontSize: 14,
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              borderColor: BUTTON_COLORS.delete.hoverBorder,
              bgcolor: BUTTON_COLORS.delete.hoverBg,
            },
          }}
        >
          Delete ({selectedIds.length})
        </Button>
      )}
      {!allTerminal && (
        <Button
          variant="contained"
          startIcon={<CancelRounded />}
          onClick={onCancel}
          sx={{
            height: 40,
            borderRadius: "96px",
            bgcolor: BUTTON_COLORS.cancel.bg,
            color: BUTTON_COLORS.cancel.color,
            fontWeight: 700,
            fontSize: 14,
            textTransform: "none",
            whiteSpace: "nowrap",
            boxShadow: "none",
            "&:hover": {
              bgcolor: BUTTON_COLORS.cancel.hoverBg,
              boxShadow: "none",
            },
          }}
        >
          Cancel ({selectedIds.length})
        </Button>
      )}
      {!allTerminalOrRequestMore && (
        <Button
          variant="contained"
          startIcon={<HelpOutlined />}
          onClick={onRequestBulk}
          sx={{
            height: 40,
            borderRadius: "96px",
            bgcolor: BUTTON_COLORS.request.bg,
            color: BUTTON_COLORS.request.color,
            fontWeight: 700,
            fontSize: 14,
            textTransform: "none",
            whiteSpace: "nowrap",
            boxShadow: "none",
            "&:hover": {
              bgcolor: BUTTON_COLORS.request.hoverBg,
              boxShadow: "none",
            },
          }}
        >
          Request More Details ({selectedIds.length})
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={<DownloadRounded />}
        onClick={onDownloadXml}
        sx={{
          height: 40,
          borderRadius: "96px",
          bgcolor: BUTTON_COLORS.download.bg,
          color: BUTTON_COLORS.download.color,
          fontWeight: 700,
          fontSize: 14,
          textTransform: "none",
          whiteSpace: "nowrap",
          boxShadow: "none",
          "&:hover": {
            bgcolor: BUTTON_COLORS.download.hoverBg,
            boxShadow: "none",
          },
        }}
      >
        Download XML ({selectedIds.length})
      </Button>
      {!allTerminal && (
        <Button
          variant="contained"
          startIcon={<CheckCircleOutlined />}
          onClick={onApproveBulk}
          sx={{
          height: 40,
          borderRadius: "100px",
          bgcolor: BUTTON_COLORS.approve.bg,
          color: BUTTON_COLORS.approve.color,
          fontWeight: 700,
          fontSize: 16,
          textTransform: "none",
          whiteSpace: "nowrap",
          boxShadow: "none",
          "&:hover": {
            bgcolor: BUTTON_COLORS.approve.hoverBg,
            boxShadow: "none",
          },
        }}
      >
          Order Created{" "}
          <span style={{ fontWeight: 400 }}>({selectedIds.length})</span>
        </Button>
      )}
    </Box>
  );
};
